import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import NavBar from './NavBar.jsx';
import NavPopup from './NavPopup.jsx';

const isMobile = () => window.innerWidth < 1220;

export default function NavContainer({ navItems }) {
    const [path, setPath] = useState([]);
    const [mobile, setMobile] = useState(isMobile());
    const [mobileOpen, setMobileOpen] = useState(false);
    const [federationMenuHovered, setFederationMenuHovered] = useState(false);
    const [navItemHovered, setNavItemHoveredRaw] = useState(false);
    const [navbarBrandHovered, setNavbarBrandHovered] = useState(false);
    const containerRef = useRef(null);
    let hideTimeoutRef;

    // Use refs to track current hover states for synchronous access in timeouts
    const hoverStatesRef = useRef({
        federationMenuHovered: false,
        navItemHovered: false,
        navbarBrandHovered: false
    });

    // Keep refs in sync with state changes
    useEffect(() => {
        hoverStatesRef.current = {
            federationMenuHovered,
            navItemHovered,
            navbarBrandHovered
        };
    }, [federationMenuHovered, navItemHovered, navbarBrandHovered]);

    // Wrapper for navItemHovered
    const setNavItemHovered = (value) => {
        setNavItemHoveredRaw(value);
    };

    // Clear path only if no nav items, federation menu, navbar brand, or popup is hovered
    const clearPathWithDelay = () => {
        if (hideTimeoutRef) {
            clearTimeout(hideTimeoutRef);
        }

        hideTimeoutRef = setTimeout(() => {
            const currentStates = hoverStatesRef.current;

            if (!currentStates.federationMenuHovered && !currentStates.navItemHovered && !currentStates.navbarBrandHovered) {
                setPath([]);
            }
        }, 300);
    };

    // Get federation menu data from the script tag
    // Returns an object: { items: [], content: { headline, short, image_url } }
    const getFederationMenuData = () => {
        try {
            // Try multiple selectors in case the script tag gets moved or modified
            let scriptTag = document.getElementById('federation-menu-data');

            if (!scriptTag) {
                return { items: [], content: null };
            }

            const content = scriptTag.textContent || scriptTag.innerText || '';
            if (!content.trim()) {
                return { items: [], content: null };
            }

            const data = JSON.parse(content.trim());
            // Support both legacy array-only payloads and new object payloads
            if (Array.isArray(data)) {
                return { items: data, content: null };
            }
            if (data && Array.isArray(data.items)) {
                return { items: data.items, content: data.content || null };
            }
            return { items: [], content: null };
        } catch (e) {
            console.error('Error loading federation menu data:', e.message);
            return { items: [], content: null };
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setMobile(isMobile());
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        // Handle navbar-brand hover for federation menu (only on desktop)
        const navbarBrand = document.querySelector('.navbar-brand[data-federation-menu]');
        if (!navbarBrand) return;

        const handleBrandMouseEnter = () => {
            // Only handle federation menu on desktop, mobile uses dropdown
            if (mobile) return;
            setNavbarBrandHovered(true);
            hoverStatesRef.current.navbarBrandHovered = true;

            // Clear any pending hide timeout
            if (hideTimeoutRef) {
                clearTimeout(hideTimeoutRef);
                hideTimeoutRef = null;
            }

            // Get fresh federation menu data each time
            const { items: currentFederationItems, content: federationContent } = getFederationMenuData();

            if (currentFederationItems.length > 0) {
                // Always show federation menu, even if regular nav is active
                const federationMenuItem = {
                    id: 'federation-menu',
                    name: 'Bund FeG',
                    url: '#',
                    federationMenu: true,
                    children: currentFederationItems,
                    hasChildren: currentFederationItems.length > 0,
                    federationContent,
                };
                setPath([federationMenuItem]);
            }
        };

        const handleBrandMouseLeave = () => {
            setNavbarBrandHovered(false);
            hoverStatesRef.current.navbarBrandHovered = false;
            // Delay hiding to allow mouse to move to popup
            clearPathWithDelay();
        };

        navbarBrand.addEventListener('mouseenter', handleBrandMouseEnter);
        navbarBrand.addEventListener('mouseleave', handleBrandMouseLeave);

        return () => {
            navbarBrand.removeEventListener('mouseenter', handleBrandMouseEnter);
            navbarBrand.removeEventListener('mouseleave', handleBrandMouseLeave);
        };
    }, []); // No dependencies needed since we read data fresh each time

    // Update navbar brand CSS class when hover states change (desktop only)
    useEffect(() => {
        const navbarBrand = document.querySelector('.navbar-brand[data-federation-menu]');
        if (!navbarBrand) return;

        // On mobile, federation menu is part of dropdown, so don't show 'open' class
        if (mobile) {
            navbarBrand.classList.remove('open');
            return;
        }

        // Check if current path contains federation menu
        const hasFederationMenu = path.length > 0 && path[0]?.federationMenu;

        // Add 'open' class when navbar brand is hovered OR federation menu popup is hovered and federation menu is active
        if (navbarBrandHovered || (federationMenuHovered && hasFederationMenu)) {
            navbarBrand.classList.add('open');
        } else {
            navbarBrand.classList.remove('open');
        }
    }, [navbarBrandHovered, federationMenuHovered, path, mobile]);

    useEffect(() => {
        const handleTogglerClick = () => {
            setMobileOpen((prev) => {
                if (prev) {
                    setPath([]);
                }
                return !prev;
            });
        };

        // Find toggler elements in the DOM
        const togglers = document.querySelectorAll('.navbar-toggler-right');
        togglers.forEach(toggler => {
            toggler.addEventListener('click', handleTogglerClick);
        });

        return () => {
            togglers.forEach(toggler => {
                toggler.removeEventListener('click', handleTogglerClick);
            });
        };
    }, []);


    const openPath = (newPath) => {
        // Always clear any pending timeout first
        if (hideTimeoutRef) {
            clearTimeout(hideTimeoutRef);
            hideTimeoutRef = null;
        }

        if (newPath.length == 0) {
            clearPathWithDelay();
        } else {
            setPath([...newPath]);
        }
    };

    // For mobile, include federation menu as a nav item
    const mobileNavItems = [...navItems];
    if (mobile) {
        const { items: federationMenuItems, content: federationContent } = getFederationMenuData();
        if (federationMenuItems.length > 0) {
            const federationMenuItem = {
                id: 'federation-menu',
                name: 'Bund FeG',
                url: '#',
                federationMenu: true,
                children: federationMenuItems,
                hasChildren: federationMenuItems.length > 0,
                federationContent,
                classes: 'federation-menu-mobile' // Distinct class for mobile styling
            };
            mobileNavItems.push(federationMenuItem);
        }
    }

    const mobileNavRoot = {
        name: "",
        url: "",
        parents: [],
        hasChildren: true,
        children: mobileNavItems,
    };

    const currentPath = (
        mobile && mobileOpen ? [mobileNavRoot] : []
    ).concat(path);

    const handlePopupMouseEnter = () => {
        setFederationMenuHovered(true);
        hoverStatesRef.current.federationMenuHovered = true;
        // Clear any pending hide timeout when entering popup
        if (hideTimeoutRef) {
            clearTimeout(hideTimeoutRef);
            hideTimeoutRef = null;
        }
    };

    const handlePopupMouseLeave = () => {
        setFederationMenuHovered(false);
        hoverStatesRef.current.federationMenuHovered = false;
        // Use unified path clearing that checks all hover states
        clearPathWithDelay();
    };

    const handleMouseEnter = (item, hasChildren) => {
        if (!hasChildren) {
            // Immediately clear path for childless items
            setPath([]);
            if (hideTimeoutRef) {
                clearTimeout(hideTimeoutRef);
                hideTimeoutRef = null;
            }
        }
    };

    const handleMouseLeave = (item, hasChildren) => {
        if (!hasChildren) {
            // Immediately clear path for childless items
            setPath([]);
            if (hideTimeoutRef) {
                clearTimeout(hideTimeoutRef);
                hideTimeoutRef = null;
            }
        } else {
            // For items with children, trigger the delayed clear through openPath
            openPath([]);
        }
    };

    return (
        <div ref={containerRef} className="nav-container">
            <NavBar 
                items={navItems} 
                path={currentPath} 
                openPath={openPath} 
                onNavItemHover={setNavItemHovered}
                onNavItemMouseEnter={handleMouseEnter}
                onNavItemMouseLeave={handleMouseLeave}
            />
            <NavPopup
                path={currentPath}
                openPath={openPath}
                onMouseEnter={handlePopupMouseEnter}
                onMouseLeave={handlePopupMouseLeave}
                isMobile={mobile}
            />
        </div>
    );
};

NavContainer.propTypes = {
    navItems: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            url: PropTypes.string,
            active: PropTypes.bool,
            activeAncestor: PropTypes.bool,
            hasChildren: PropTypes.bool,
            children: PropTypes.array,
        })
    ).isRequired,
};